import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from './ui/dropdown-menu';
import Logo from './Logo';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' }
  ];

  const visaCountries = [
    { name: 'Dubai Visa', path: '/visa/dubai' },
    { name: 'Thailand Visa', path: '/visa/thailand' },
    { name: 'Singapore Visa', path: '/visa/singapore' },
    { name: 'Schengen Visa', path: '/visa/schengen' },
    { name: 'UK Visa', path: '/visa/uk' },
    { name: 'Canada Visa', path: '/visa/canada' }
  ];

  const destinations = [
    { name: 'Kashmir', path: '/destinations/kashmir' },
    { name: 'Sikkim', path: '/destinations/sikkim' },
    { name: 'Bali', path: '/destinations/bali' },
    { name: 'Dubai', path: '/destinations/dubai' },
    { name: 'Thailand', path: '/destinations/thailand' },
    { name: 'Vietnam', path: '/destinations/vietnam' }
  ];

  const rightLinks = [
    { name: 'Flights', path: '/flights' },
    { name: 'Hotels', path: '/hotels' },
    { name: 'Thailand DMC', path: '/thailand-dmc' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;
  const isPathActive = (prefix) => location.pathname.startsWith(prefix);

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50" data-testid="site-header">
      {/* Top bar */}
      <div className="bg-gradient-to-r from-[#2D8DB4] to-[#F7945D] text-white text-xs md:text-sm">
        <div className="container mx-auto px-4 py-2 flex flex-wrap justify-between items-center gap-2">
          <div className="flex flex-wrap items-center gap-4">
            <a href="tel:+919016930091" className="flex items-center gap-1 hover:opacity-80" data-testid="topbar-phone-1">
              <Phone size={13} /> +91 90169 30091
            </a>
            <a href="tel:+917388813555" className="hidden sm:flex items-center gap-1 hover:opacity-80" data-testid="topbar-phone-2">
              <Phone size={13} /> +91 73888 13555
            </a>
            <a href="mailto:info@varnivisa.com" className="hidden md:flex items-center gap-1 hover:opacity-80">
              <Mail size={13} /> info@varnivisa.com
            </a>
          </div>
          <span className="hidden md:inline">Surat&apos;s Trusted Visa & Travel Consultant · Powered by Varni2Fly Thailand</span>
        </div>
      </div>

      {/* Main nav */}
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center py-3">
          <Logo />

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(link.path) ? 'text-[#2D8DB4] bg-blue-50' : 'text-gray-700 hover:text-[#2D8DB4] hover:bg-gray-50'
                }`}
              >
                {link.name}
              </Link>
            ))}

            {/* Visa Services dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-visas"
                  className={`px-3 py-2 text-sm font-medium rounded-md flex items-center gap-1 transition-colors ${
                    isPathActive('/visa') ? 'text-[#2D8DB4] bg-blue-50' : 'text-gray-700 hover:text-[#2D8DB4] hover:bg-gray-50'
                  }`}
                >
                  Visa Services <ChevronDown size={14} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuItem asChild>
                  <Link to="/visa-services" className="font-semibold">All Visa Services</Link>
                </DropdownMenuItem>
                {visaCountries.map((v) => (
                  <DropdownMenuItem key={v.path} asChild>
                    <Link to={v.path} data-testid={`nav-visa-${v.path.split('/').pop()}`}>{v.name}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Tour Packages dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-tours"
                  className={`px-3 py-2 text-sm font-medium rounded-md flex items-center gap-1 transition-colors ${
                    isPathActive('/tour-packages') || isPathActive('/destinations') ? 'text-[#2D8DB4] bg-blue-50' : 'text-gray-700 hover:text-[#2D8DB4] hover:bg-gray-50'
                  }`}
                >
                  Tour Packages <ChevronDown size={14} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuItem asChild>
                  <Link to="/tour-packages" className="font-semibold">All Tour Packages</Link>
                </DropdownMenuItem>
                {destinations.map((d) => (
                  <DropdownMenuItem key={d.path} asChild>
                    <Link to={d.path} data-testid={`nav-dest-${d.path.split('/').pop()}`}>{d.name}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {rightLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(link.path) ? 'text-[#2D8DB4] bg-blue-50' : 'text-gray-700 hover:text-[#2D8DB4] hover:bg-gray-50'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-2">
            <Link to="/b2b/login" data-testid="header-b2b" className="text-xs font-semibold text-[#2D8DB4] hover:text-[#F7945D] uppercase tracking-wider border border-[#2D8DB4]/30 px-3 py-2 rounded-md hover:border-[#F7945D]/60 transition-colors">
              B2B Portal
            </Link>
            <a href="https://wa.me/919016930091" target="_blank" rel="noopener noreferrer" data-testid="header-whatsapp">
              <Button className="bg-[#25D366] hover:bg-[#1fb855] text-white">WhatsApp Now</Button>
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 -mr-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="lg:hidden pb-4 border-t pt-3" data-testid="mobile-menu">
            <div className="flex flex-col gap-1 max-h-[70vh] overflow-y-auto">
              {[...navLinks, ...rightLinks, { name: 'All Visa Services', path: '/visa-services' }, { name: 'All Tour Packages', path: '/tour-packages' }].map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-2 rounded-md ${isActive(link.path) ? 'bg-[#2D8DB4] text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="border-t my-2" />
              <div className="px-4 text-xs uppercase tracking-wider text-gray-500 py-1">Popular Visas</div>
              {visaCountries.map((v) => (
                <Link key={v.path} to={v.path} onClick={() => setMobileMenuOpen(false)} className="px-4 py-2 text-sm hover:bg-gray-100 rounded">
                  {v.name}
                </Link>
              ))}
              <div className="border-t my-2" />
              <div className="px-4 text-xs uppercase tracking-wider text-gray-500 py-1">Destinations</div>
              {destinations.map((d) => (
                <Link key={d.path} to={d.path} onClick={() => setMobileMenuOpen(false)} className="px-4 py-2 text-sm hover:bg-gray-100 rounded">
                  {d.name}
                </Link>
              ))}
              <a href="https://wa.me/919016930091" target="_blank" rel="noopener noreferrer" className="mt-3 mx-2">
                <Button className="w-full bg-[#25D366] hover:bg-[#1fb855] text-white">WhatsApp Now</Button>
              </a>
              <Link to="/b2b/login" onClick={() => setMobileMenuOpen(false)} className="mt-2 mx-2">
                <Button variant="outline" className="w-full">B2B Agent Portal</Button>
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
